import { useContext, useEffect, useMemo, useState } from "react";

import { AdditionalWidgetData } from "@/types/additional-widget-data";
import { WidgetContentContext } from "@/features/dashboard/components/widget-container-card";
import { HighCharts } from "@/features/shared/components";
import { useDashboardData } from "@/features/shared/hooks";
import useRefetchWidget from "@/features/shared/widgets/hooks/useRefetchWidget";
import useLayoutStore from "@/store/useLayoutStore";

import * as utils from "./time-series.utils";

type Props = {
  id: string;
  data: AdditionalWidgetData;
};

const TimeSeries = ({ id, data }: Props) => {
  const { selectedTimeRange: timeRange } = useDashboardData();

  const widgetContentContext = useContext(WidgetContentContext);
  const widgetContentRef = widgetContentContext.ref;
  const [isFullscreen] = useLayoutStore((s) => s.context.isFullscreen);

  const series = data.data;

  // keep state for chart height to force the graph to resize on fullscreen
  const [chartHeight, setChartHeight] = useState(() => {
    return widgetContentRef?.current?.offsetHeight;
  });
  useEffect(() => {
    setChartHeight(widgetContentRef?.current?.offsetHeight);
  }, [isFullscreen]);

  const chartConfig = useMemo(() => {
    const yAxis = utils.generateYAxis(series);
    const xAxis = utils.getChartXAxis();
    const tooltip = utils.getChartTooltip(series);
    const seriesData = utils.generateChartSeries(series, timeRange);

    const config: Highcharts.Options = {
      title: undefined,
      xAxis,
      yAxis,
      tooltip,
      series: seriesData,
      plotOptions: {
        series: {
          animation: false,
        },
      },
      chart: {
        height: chartHeight,
      },
      legend: {
        padding: 0,
      },
    };

    return config;
  }, [chartHeight, timeRange, series]);

  useRefetchWidget(id);

  return <HighCharts options={chartConfig} />;
};

export default TimeSeries;
